// One-at-a-time audio player for source / prepped / needle outputs.
export interface Clip {
  id: string;
  label: string;
  color: string;
  channels: Float32Array[];
  sampleRate: number;
  /** Seconds of silent lead-in on the track before the audio starts (for the playhead). */
}

export class Player {
  private ctx: AudioContext | null = null;
  private node: AudioBufferSourceNode | null = null;
  private playingId: string | null = null;
  private startedAt = 0;
  monoSum = false;
  onChange?: (playingId: string | null) => void;

  get playing() {
    return this.playingId;
  }

  /** Seconds since playback started, or null. */
  position(): number | null {
    return this.ctx && this.playingId ? this.ctx.currentTime - this.startedAt : null;
  }

  stop() {
    if (this.node) {
      this.node.onended = null;
      try { this.node.stop(); } catch { /* already stopped */ }
      this.node.disconnect();
    }
    this.node = null;
    this.playingId = null;
    this.onChange?.(null);
  }

  toggle(clip: Clip) {
    const was = this.playingId;
    this.stop();
    if (was === clip.id) return;
    this.ctx ??= new AudioContext({ sampleRate: clip.sampleRate });
    const ctx = this.ctx;
    const nch = clip.channels.length;
    const buf = ctx.createBuffer(2, clip.channels[0].length, clip.sampleRate);
    const L = clip.channels[0], R = clip.channels[nch - 1];
    if (this.monoSum) {
      const m = new Float32Array(L.length);
      for (let i = 0; i < L.length; i++) m[i] = (L[i] + R[i]) / 2;
      buf.copyToChannel(m, 0);
      buf.copyToChannel(m, 1);
    } else {
      buf.copyToChannel(L as Float32Array<ArrayBuffer>, 0);
      buf.copyToChannel(R as Float32Array<ArrayBuffer>, 1);
    }
    const node = ctx.createBufferSource();
    node.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = 0.8;
    node.connect(gain).connect(ctx.destination);
    node.onended = () => { if (this.node === node) this.stop(); };
    void ctx.resume();
    node.start();
    this.startedAt = ctx.currentTime;
    this.node = node;
    this.playingId = clip.id;
    this.onChange?.(clip.id);
  }
}
